import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';

const API_URL = API;

// Log the API URL for debugging
//
//

/**
 * Garbage Truck Schedule Form Component
 * For creating and editing garbage truck collection schedules
 */
const BusScheduleForm = ({ schedule = null, towns = [], routes = [], routeNames = [], getTownsForRoute, handleRouteSelection, onClose, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    company_name: schedule?.company_name || '',
    contact_phone: schedule?.contact_phone || '',
    contact_email: schedule?.contact_email || '',
    route_name: schedule?.route_name || '',
    origin_town_id: schedule?.origin_town_id || '',
    destination_town_id: schedule?.destination_town_id || '',
    depart_time: schedule?.depart_time || '07:00',
    arrive_time: schedule?.arrive_time || '09:00',
    service_type: schedule?.service_type || 'Regular',
    days_of_week: schedule?.days_of_week || {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false
    },
    stops: schedule?.stops || [],
    fare_price: schedule?.fare_price || '',
    bus_capacity: schedule?.bus_capacity || '',
    special_notes: schedule?.special_notes || '',
    is_active: schedule?.is_active !== undefined ? schedule.is_active : true
  });

  // Auto-fill company information from logged-in user when creating new schedule
  useEffect(() => {
    const fetchUserData = async () => {
      // Only auto-fill when creating a new schedule (not editing)
      if (schedule) return;

      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const userData = response.data;

        // Auto-fill company information if available
        setFormData(prev => ({
          ...prev,
          company_name: userData.company_name || prev.company_name,
          contact_phone: userData.phone_number || prev.contact_phone,
          contact_email: userData.email || prev.contact_email
        }));

      } catch (error) {
        // Silently fail - user can still fill in manually
      }
    };

    fetchUserData();
  }, [schedule]);

  const getTownName = (townId) => {
    const town = towns.find(t => t.id === townId);
    return town ? town.name : 'Unknown Town';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDayChange = (day, checked) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: {
        ...prev.days_of_week,
        [day]: checked
      }
    }));
  };

  const addStop = () => {
    setFormData(prev => ({
      ...prev,
      stops: [
        ...prev.stops,
        {
          stop_name: '',
          town_id: '',
          arrival_time: '08:00',
          is_major_stop: true
        }
      ]
    }));
  };

  const removeStop = (index) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index)
    }));
  };

  const handleStopChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.map((stop, i) => 
        i === index ? { ...stop, [field]: value } : stop
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!formData.company_name.trim()) {
      setError('Company name is required');
      setLoading(false);
      return;
    }

    if (!formData.route_name.trim()) {
      setError('Route name is required');
      setLoading(false);
      return;
    }

    if (!formData.origin_town_id || !formData.destination_town_id) {
      setError('Origin and destination towns are required');
      setLoading(false);
      return;
    }

    if (formData.origin_town_id === formData.destination_town_id) {
      setError('Origin and destination towns must be different');
      setLoading(false);
      return;
    }

    if (formData.depart_time >= formData.arrive_time) {
      setError('Departure time must be before arrival time');
      setLoading(false);
      return;
    }

    // Check if at least one day is selected
    const selectedDays = Object.values(formData.days_of_week).some(day => day);
    if (!selectedDays) {
      setError('Please select at least one day of operation');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Prepare data for submission
      const submitData = {
        ...formData,
        fare_price: formData.fare_price ? parseFloat(formData.fare_price) : null,
        bus_capacity: formData.bus_capacity ? parseInt(formData.bus_capacity) : null
      };

      let response;
      if (schedule) {
        // Update existing schedule
        response = await axios.put(
          `${API_URL}/bus-schedules/${schedule.id}`,
          submitData,
          { headers }
        );
      } else {
        // Create new schedule
        response = await axios.post(
          `${API_URL}/bus-schedules/`,
          submitData,
          { headers }
        );
      }

      alert(`Truck schedule ${schedule ? 'updated' : 'created'} successfully!`);
      onSubmit(response.data);
      onClose();

    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save truck schedule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-screen overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">
          {schedule ? 'Edit Truck Schedule' : 'Create New Truck Schedule'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Information */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Company Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => handleInputChange('contact_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Route Information */}
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Route Information</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Route *
                </label>
                <select
                  value={formData.route_name}
                  onChange={(e) => {
                    handleRouteSelection(e.target.value, formData, setFormData);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a route...</option>
                  {(() => {
                    //
                    //
                    //
                    
                    if (routeNames.length > 0) {
                      //
                      return routeNames.map(routeName => {
                        //
                        return (
                          <option key={routeName} value={routeName}>
                            {routeName}
                          </option>
                        );
                      });
                    } else {
                      //
                      return <option disabled>No routes available - Contact admin to create routes</option>;
                    }
                  })()}
                </select>
                {routeNames.length === 0 && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-700">
                      📋 <strong>No routes found!</strong> Routes must be created by an administrator first.
                      <br />
                      Contact your system administrator to set up routes with areas before creating truck schedules.
                    </p>
                    <button
                      type="button"
                      onClick={async () => {
                        //
                        try {
                          const testResponse = await axios.get(`${API_URL}/public/towns/routes/names`);
                          //
                          alert(`Manual API Test Result: ${JSON.stringify(testResponse.data)}`);
                        } catch (testError) {
                          //
                          alert(`Manual API Test Failed: ${testError.message}`);
                        }
                      }}
                      className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    >
                      🧪 Test API Manually
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Routes are created by administrators in the Town Management system
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Origin Town *
                  </label>
                  <select
                    value={formData.origin_town_id}
                    onChange={(e) => handleInputChange('origin_town_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select origin town...</option>
                    {formData.route_name ? 
                      getTownsForRoute(formData.route_name)
                        .sort((a, b) => a.sequence_number - b.sequence_number)
                        .map(town => (
                          <option key={town.id} value={town.id}>
                            #{town.sequence_number} {town.name}
                          </option>
                        ))
                      : []
                    }
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Destination Town *
                  </label>
                  <select
                    value={formData.destination_town_id}
                    onChange={(e) => handleInputChange('destination_town_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select destination town...</option>
                    {formData.route_name ? 
                      getTownsForRoute(formData.route_name)
                        .filter(town => town.id !== formData.origin_town_id)
                        .sort((a, b) => a.sequence_number - b.sequence_number)
                        .map(town => (
                          <option key={town.id} value={town.id}>
                            #{town.sequence_number} {town.name}
                          </option>
                        ))
                      : []
                    }
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Schedule Details */}
          <div className="bg-purple-50 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Schedule Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Departure Time *
                </label>
                <input
                  type="time"
                  value={formData.depart_time}
                  onChange={(e) => handleInputChange('depart_time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Arrival Time *
                </label>
                <input
                  type="time"
                  value={formData.arrive_time}
                  onChange={(e) => handleInputChange('arrive_time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Type
                </label>
                <select
                  value={formData.service_type}
                  onChange={(e) => handleInputChange('service_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Regular">Regular</option>
                  <option value="Express">Express</option>
                </select>
              </div>
            </div>

            {/* Days of Operation */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Days of Operation *
              </label>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                {Object.entries(formData.days_of_week).map(([day, checked]) => (
                  <label key={day} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => handleDayChange(day, e.target.checked)}
                      className="mr-2"
                    />
                    <span className="capitalize">{day.slice(0, 3)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Intermediate Stops */}
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-gray-800">Intermediate Stops</h4>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={async () => {
                    if (!formData.route_name) {
                      setError('Please select a route first');
                      return;
                    }
                    if (!formData.origin_town_id || !formData.destination_town_id) {
                      setError('Please select origin and destination towns first');
                      return;
                    }
                    if (!formData.depart_time) {
                      setError('Please select a departure time first');
                      return;
                    }

                    try {
                      setLoading(true);
                      setError('');

                      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

                      // First, try to calculate average stop times based on historical data
                      let useHistoricalData = false;
                      try {
                        const avgTimesResponse = await axios.post(
                          `${API_URL}/bus-schedules/calculate-average-stop-times`,
                          {
                            origin_town_id: formData.origin_town_id,
                            destination_town_id: formData.destination_town_id,
                            departure_time: formData.depart_time,
                            days_of_week: formData.days_of_week,
                            route_name: formData.route_name
                          },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );

                        if (avgTimesResponse.data.data_available &&
                            (avgTimesResponse.data.stops.length > 0 || avgTimesResponse.data.origin_time || avgTimesResponse.data.destination_time)) {
                          // Use historical data with calculated average times
                          const stopsWithAvgTimes = avgTimesResponse.data.stops.map(stop => ({
                            stop_name: stop.town_name,
                            town_id: stop.town_id,
                            arrival_time: stop.arrival_time,
                            is_major_stop: false
                          }));

                          // Only update stops with calculated average times
                          // Don't overwrite the user's manually-entered departure/arrival times
                          setFormData(prev => ({
                            ...prev,
                            stops: stopsWithAvgTimes
                          }));

                          useHistoricalData = true;

                          // Show success message
                          const daysCount = avgTimesResponse.data.stops[0]?.days_count || 'multiple';
                          setTimeout(() => {
                            alert(`Auto-filled with calculated average times based on ${daysCount} days of historical GPS data!\n\n` +
                                  `- Departure time: ${avgTimesResponse.data.origin_time || 'N/A'}\n` +
                                  `- Arrival time: ${avgTimesResponse.data.destination_time || 'N/A'}\n` +
                                  `- Intermediate stops: ${stopsWithAvgTimes.length}`);
                          }, 100);
                        }
                      } catch (avgError) {
                        // Continue to fallback method
                      }

                      // If historical data not available, fall back to route-based auto-populate
                      if (!useHistoricalData) {
                        const response = await axios.post(
                          `${API_URL}/public/towns/routes/${encodeURIComponent(formData.route_name)}/auto-populate-stops?origin_town_id=${formData.origin_town_id}&destination_town_id=${formData.destination_town_id}`
                        );

                        if (response.data.stops && response.data.stops.length > 0) {
                          setFormData(prev => ({
                            ...prev,
                            stops: response.data.stops
                          }));

                          // Clear any previous errors
                          setError('');

                          // Show success message
                          const direction = response.data.direction === 'forward' ? '→' : '←';
                          setTimeout(() => {
                            alert(`Auto-populated ${response.data.stops.length} intermediate stops (${direction})\n\nNote: No historical GPS data available. Please manually adjust arrival times.`);
                          }, 100);
                        } else {
                          alert('No intermediate stops found between these towns.');
                        }
                      }

                      // Clear any previous errors
                      setError('');

                    } catch (error) {
                      setError(error.response?.data?.detail || 'Failed to auto-populate stops');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm disabled:opacity-50"
                  disabled={loading || !formData.route_name || !formData.origin_town_id || !formData.destination_town_id || !formData.depart_time}
                  title="Automatically populate stops with calculated average arrival times based on your historical GPS data"
                >
                  🪄 Auto-Fill
                </button>
                <button
                  type="button"
                  onClick={addStop}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  + Add Stop
                </button>
              </div>
            </div>

            {formData.stops.length === 0 ? (
              <p className="text-gray-600 text-sm italic">No stops added. Add collection points to show areas covered by this route.</p>
            ) : (
              <div className="space-y-3">
                {formData.stops.map((stop, index) => (
                  <div key={index} className="border border-gray-200 rounded-md p-3 bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Stop Name</label>
                        <input
                          type="text"
                          value={stop.stop_name}
                          onChange={(e) => handleStopChange(index, 'stop_name', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Collection Point Name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Town</label>
                        <select
                          value={stop.town_id}
                          onChange={(e) => handleStopChange(index, 'town_id', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          <option value="">Select town...</option>
                          {formData.route_name ? 
                            getTownsForRoute(formData.route_name)
                              .sort((a, b) => a.sequence_number - b.sequence_number)
                              .map(town => (
                                <option key={town.id} value={town.id}>
                                  #{town.sequence_number} {town.name}
                                </option>
                              ))
                            : []
                          }
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Arrival Time</label>
                        <input
                          type="time"
                          value={stop.arrival_time}
                          onChange={(e) => handleStopChange(index, 'arrival_time', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={stop.is_major_stop}
                            onChange={(e) => handleStopChange(index, 'is_major_stop', e.target.checked)}
                            className="mr-1"
                          />
                          Major Stop
                        </label>
                        <button
                          type="button"
                          onClick={() => removeStop(index)}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Additional Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Additional Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fare Price ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.fare_price}
                  onChange={(e) => handleInputChange('fare_price', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Truck Capacity (tons)
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.bus_capacity}
                  onChange={(e) => handleInputChange('bus_capacity', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="10"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Notes
                </label>
                <textarea
                  value={formData.special_notes}
                  onChange={(e) => handleInputChange('special_notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Any special notes about this route (e.g., luggage restrictions, advance booking required, etc.)"
                />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="font-medium text-gray-700">Active Schedule (visible to public)</span>
                </label>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Form Buttons */}
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading 
                ? (schedule ? 'Updating...' : 'Creating...') 
                : (schedule ? 'Update Schedule' : 'Create Schedule')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Truck Schedules View Component
 * For managing garbage truck collection schedules
 */
const BusSchedulesView = () => {
  const [schedules, setSchedules] = useState([]);
  const [towns, setTowns] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [routeNames, setRouteNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);

  //
  //

  useEffect(() => {
    //
    //
    //
    loadData();
  }, []);

  // Monitor routeNames state changes
  // useEffect(() => 
  //   {
  //
  //
  // }, 
  // [routeNames]);

  const loadData = async () => {
    //
    
    try {
      // Load each data source individually to avoid Promise.all failures
      await loadSchedules().catch(err => {});
      await loadTowns().catch(err => {});
      await loadRoutes().catch(err => {});
      await loadRouteNames().catch(err => {});
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadRoutes = async () => {
    try {
      // Load all towns and group them by route on the frontend
      const response = await axios.get(`${API_URL}/public/towns?is_active=true&sort_by=sequence_number`);
      const towns = response.data;
      
      // Group towns by route_name
      const routesMap = {};
      towns.forEach(town => {
        if (!routesMap[town.route_name]) {
          routesMap[town.route_name] = {
            route_name: town.route_name,
            towns: [],
            total_towns: 0
          };
        }
        routesMap[town.route_name].towns.push(town);
        routesMap[town.route_name].total_towns++;
      });
      
      const routesArray = Object.values(routesMap);
      setRoutes(routesArray);
      return routesArray;
    } catch (error) {
      setRoutes([]);
      return [];
    }
  };

  const loadRouteNames = async () => {
    try {
      //
      //
      
      const response = await axios.get(`${API_URL}/public/towns/routes/names`);
      //
      //
      //
      //
      
      const routeNames = response.data.route_names || [];
      //
      //
      //
      
      // Force state update
      //
      setRouteNames(routeNames);
      
      // Verify state was set
      //
      
      return routeNames;
    } catch (error) {
      
      setRouteNames([]);
      return [];
    }
  };

  const loadSchedules = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      //
      const response = await axios.get(`${API_URL}/bus-schedules/`, { headers });
      //
      setSchedules(response.data);
      return response.data;
    } catch (error) {
      setSchedules([]);
      return [];
    }
  };

  const loadTowns = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/towns?is_active=true&sort_by=sequence_number`);
      setTowns(response.data);
      return response.data;
    } catch (error) {
      // Fallback towns
      const fallbackTowns = [
        { id: 'belize_city', name: 'Belize City', sequence_number: 1 },
        { id: 'orange_walk', name: 'Orange Walk', sequence_number: 2 },
        { id: 'corozal', name: 'Corozal', sequence_number: 3 },
        { id: 'san_ignacio', name: 'San Ignacio', sequence_number: 4 },
        { id: 'dangriga', name: 'Dangriga', sequence_number: 5 },
        { id: 'punta_gorda', name: 'Punta Gorda', sequence_number: 6 }
      ];
      setTowns(fallbackTowns);
      return fallbackTowns;
    }
  };

  const handleCreateSchedule = () => {
    setEditingSchedule(null);
    setShowForm(true);
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setShowForm(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this truck schedule? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(`${API_URL}/bus-schedules/${scheduleId}`, { headers });

      alert('Truck schedule deleted successfully!');
      loadSchedules(); // Reload the schedules list
    } catch (error) {
      alert('Failed to delete truck schedule. Please try again.');
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingSchedule(null);
  };

  const handleFormSubmit = (scheduleData) => {
    //
    loadSchedules(); // Reload the schedules list
  };

  const getTownName = (townId) => {
    const town = towns.find(t => t.id === townId);
    return town ? town.name : 'Unknown Town';
  };

  const getTownsForRoute = (routeName) => {
    return towns.filter(town => town.route_name === routeName);
  };

  const handleRouteSelection = (selectedRouteName, formData, setFormData) => {
    if (!selectedRouteName) return;
    
    // Update route name and clear origin/destination when route changes
    setFormData(prev => ({
      ...prev,
      route_name: selectedRouteName,
      origin_town_id: '',
      destination_town_id: '',
      stops: [] // Clear stops when route changes
    }));
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getActiveDays = (daysOfWeek) => {
    if (!daysOfWeek) return [];
    return Object.entries(daysOfWeek)
      .filter(([_, active]) => active)
      .map(([day, _]) => day.charAt(0).toUpperCase() + day.slice(1, 3));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-4xl mb-4">🚛</div>
          <p className="text-gray-600">Loading truck schedules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Truck Schedules</h2>
          <p className="text-gray-600 mt-1">Manage garbage truck collection routes and times</p>

        </div>
        <button
          onClick={handleCreateSchedule}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
        >
          + Create New Schedule
        </button>
      </div>

      {/* Schedules List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          Your Schedules ({schedules.length})
        </h3>

        {schedules.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🚛</div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">No truck schedules yet</h4>
            <p className="text-gray-600 text-sm mb-4">
              Create your first schedule to let residents know when garbage collection happens.
            </p>
            <button
              onClick={handleCreateSchedule}
              className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition-colors"
            >
              Create Your First Schedule
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {schedules.map((schedule) => (
              <div
                key={schedule.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-green-50 border-green-200"
              >
                {/* Schedule Header */}
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold text-lg text-gray-900">{schedule.route_name}</h4>
                    <p className="text-sm text-green-600 font-medium">🚛 {schedule.company_name}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditSchedule(schedule)}
                      className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteSchedule(schedule.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Schedule Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-gray-800">Route:</p>
                    <p className="text-green-700 font-semibold">
                      {getTownName(schedule.origin_town_id)} → {getTownName(schedule.destination_town_id)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      📍 {schedule.route_name}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Schedule:</p>
                    <p className="font-semibold text-blue-600">
                      {formatTime(schedule.depart_time)} - {formatTime(schedule.arrive_time)}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Service:</p>
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                      schedule.service_type === 'Express' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {schedule.service_type}
                    </span>
                  </div>
                </div>

                {/* Operating Days */}
                <div className="mt-3">
                  <p className="font-medium text-gray-800 text-sm mb-1">Operating Days:</p>
                  <div className="flex flex-wrap gap-1">
                    {getActiveDays(schedule.days_of_week).map(day => (
                      <span key={day} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {day}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Stops Information */}
                {schedule.stops && schedule.stops.length > 0 && (
                  <div className="mt-3">
                    <p className="font-medium text-gray-800 text-sm mb-1">
                      Intermediate Stops ({schedule.stops.length}):
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {schedule.stops.slice(0, 3).map((stop, index) => (
                        <span key={index} className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                          {stop.stop_name || `Stop ${index + 1}`}
                        </span>
                      ))}
                      {schedule.stops.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          +{schedule.stops.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Status */}
                <div className="mt-3">
                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                    schedule.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {schedule.is_active ? '✅ Active' : '⏸️ Inactive'}
                  </span>
                  {schedule.fare_price && (
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                      ${schedule.fare_price}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Information Panel */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">💡 Truck Schedule Tips</h3>
        <div className="text-sm text-gray-600 space-y-2">
          <p>• <strong>Accurate Times:</strong> Provide accurate start/end times for collection routes</p>
          <p>• <strong>Collection Points:</strong> Add stops between origin and destination to show collection areas</p>
          <p>• <strong>Service Type:</strong> Express routes cover fewer areas than Regular routes</p>
          <p>• <strong>Active Status:</strong> Only active schedules are visible to residents</p>
          <p>• <strong>Contact Information:</strong> Help residents reach you with phone or email contact details</p>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <BusScheduleForm
          schedule={editingSchedule}
          towns={towns}
          routes={routes}
          routeNames={routeNames}
          getTownsForRoute={getTownsForRoute}
          handleRouteSelection={handleRouteSelection}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default BusSchedulesView;